import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { Component, inject, Injector } from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxPhone } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  FormHelper,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  onyxEmailValidator,
  OnyxFormMode,
  OnyxModalComponent,
  OnyxPhoneNumberInputComponent,
  onyxPhoneValidator,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import type { EmployeeRole } from '../../../../employees/common/enums/employee-role';

interface ContractorAddContactPersonModalData {
  mode: OnyxFormMode.ADD;
  contactPerson: ContractorContactPersonFormGroup;
}

interface ContractorEditContactPersonModalData {
  mode: OnyxFormMode.EDIT;
  contactPerson: ContractorContactPersonFormGroup;
  index: number;
}

export type ContractorContactPersonModalData =
  ContractorAddContactPersonModalData | ContractorEditContactPersonModalData;

export type ContractorContactPersonFormGroup = ReturnType<
  typeof ContractorContactPersonModalComponent.buildForm
>;
export type ContractorContactPersonForm = ReturnType<
  ContractorContactPersonFormGroup['getRawValue']
>;

@Component({
  selector: 'app-contractor-contact-person-modal',
  imports: [
    OnyxModalComponent,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxDropdownComponent,
    OnyxButtonComponent,
    TranslatePipe,
    AsyncPipe,
    OnyxPhoneNumberInputComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './contractor-contact-person-modal.component.html',
  styleUrl: './contractor-contact-person-modal.component.scss',
})
export class ContractorContactPersonModalComponent {
  protected readonly data =
    inject<ContractorContactPersonModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(
    DialogRef<ContractorContactPersonFormGroup>,
  );
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'contractors.contractorForm.contactPersons.modal';
  protected readonly OnyxFormMode = OnyxFormMode;

  protected readonly roles$ = this.dictionariesService.getDictionary(
    DictionaryCode.EMPLOYEE_ROLE,
  );

  protected close$ = new Subject<
    ContractorContactPersonFormGroup | undefined
  >();

  protected async submit(): Promise<void> {
    const form = this.data.contactPerson;
    if (!(await ValidationHelper.checkValidity(form, this.injector))) return;

    this.close$.next(form);
  }

  protected cancel(): void {
    FormHelper.reset(this.data.contactPerson);
    this.close$.next(undefined);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      firstName: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      lastName: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      roles: fb.control<EmployeeRole[]>([], [Validators.required]),
      email: fb.control<string | null>(null, [
        Validators.required,
        onyxEmailValidator,
      ]),
      phone: fb.control<OnyxPhone | null>(null, [
        Validators.required,
        onyxPhoneValidator,
      ]),
      transId: fb.control<string | null>(null, [onyxTextFieldLengthValidator]),
      timocomId: fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      note: fb.control<string | null>(null, [onyxTextFieldLengthValidator]),
    });
  }
}
